import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { Recipe, RecipeStand } from "../models/Recipe";
import {
	createRecipeEmpty,
	duplicateRecipe,
	removeRecipe,
	upsertRecipe,
	upsertRecipeStand,
} from "../repositories/RecipesRepository";
import { RECIPES_QUERY_KEYS } from "./useRecipes";

interface MutationOptions {
	onSuccess?: (data?: number) => void;
	onError?: (error: Error) => void;
}

export const useUpsertRecipe = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (recipe: Recipe) => {
			return await upsertRecipe(recipe);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: RECIPES_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertRecipe: mutation.mutate,
		upsertRecipeAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};

export const useDeleteRecipe = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async (id: number) => {
			await removeRecipe(id);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: RECIPES_QUERY_KEYS.all,
			});
			onSuccess?.();
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		deleteRecipe: mutation.mutate,
		deleteRecipeAsync: mutation.mutateAsync,
		isDeleting: mutation.isPending,
	};
};

export const useCreateRecipeEmpty = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async ({
			productSizeId,
			name,
		}: {
			productSizeId: number;
			name?: string;
		}) => {
			return await createRecipeEmpty(productSizeId, name);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: RECIPES_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		createRecipeEmpty: mutation.mutate,
		createRecipeEmptyAsync: mutation.mutateAsync,
		isCreating: mutation.isPending,
	};
};

export const useDuplicateRecipe = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async ({
			recipeId,
			name,
		}: {
			recipeId: number;
			name?: string;
		}) => {
			return await duplicateRecipe(recipeId, name);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: RECIPES_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		duplicateRecipe: mutation.mutate,
		duplicateRecipeAsync: mutation.mutateAsync,
		isDuplicating: mutation.isPending,
	};
};

export const useUpsertRecipeStand = ({
	onSuccess,
	onError,
}: MutationOptions = {}) => {
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: async ({
			recipeId,
			stand,
		}: {
			recipeId: number;
			stand: RecipeStand;
		}) => {
			return await upsertRecipeStand(recipeId, stand);
		},
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: RECIPES_QUERY_KEYS.all,
			});
			onSuccess?.(data);
		},
		onError: (error: Error) => {
			onError?.(error);
		},
	});

	return {
		upsertRecipeStand: mutation.mutate,
		upsertRecipeStandAsync: mutation.mutateAsync,
		isUpserting: mutation.isPending,
	};
};
